/**
 * Diagnostic Agent question batch (owner 23 Oct: "ask all together, up to 5 questions").
 *
 * A tse:question that carries `questions` (tse_question.schema.json) is ONE card with every question of the
 * batch; QuestionCard answers it once with {kind: BATCH_ANSWER_KIND, picks: {question_id: option_id}} and the
 * reply is ONE tse:message kind "answer" with `answers` (tse_message.schema.json). One-question cards never
 * carry `questions` and keep the single-click path (questionAnswer.js).
 */

export const MAX_BATCH_QUESTIONS = 5;
export const BATCH_ANSWER_KIND = "batch";
const LINE_SEP = " — ";

/** True for a card that asks several questions at once. */
export const isBatchQuestion = (question) => Array.isArray(question?.questions) && question.questions.length > 1;

/** True for the value QuestionCard hands to onAnswer when a batch card is sent. */
export const isBatchAnswer = (answer) => !!answer && typeof answer === "object" && answer.kind === BATCH_ANSWER_KIND;

/**
 * The batch reply for a sent card, or null when the card is not a batch or a question has no valid pick.
 * Each line also keeps the options the card offered, so the transcript can show the answered card on the left.
 * @returns {{answer_to: string, answers: {question_id: string, option_id: string}[], lines: {question: string, answer: string, options: string[]}[], message: string}|null}
 */
export function buildBatchAnswer(question, answer) {
  if (!isBatchQuestion(question) || !isBatchAnswer(answer) || !question.question_id) return null;
  const answers = [];
  const lines = [];
  for (const q of question.questions) {
    const optionId = answer.picks?.[q.question_id];
    const option = (q.options || []).find((o) => o?.option_id === optionId);
    if (!option) return null;
    answers.push({ question_id: q.question_id, option_id: optionId });
    lines.push({ question: q.text, answer: option.label, options: (q.options || []).map((o) => o?.label).filter(Boolean) });
  }
  const message = lines.map((l) => `${l.question}${LINE_SEP}${l.answer}`).join("\n");
  return { answer_to: question.question_id, answers, lines, message };
}

export const DA_QUESTION_BLOCK = "da_question";
export const DA_ANSWER_BLOCK = "da_answer";

const labelOf = (options, optionId) => (options || []).find((o) => o?.option_id === optionId)?.label;

/** The label picked for each question, from the recorded answer ids, else from the stored "Question — Answer" lines. */
function pickedLabels(questions, answerBlock, userText) {
  const byId = new Map((answerBlock?.answers || []).map((a) => [a?.question_id, a?.option_id]));
  const typed = String(userText || "").split("\n");
  return questions.map((q, i) => {
    const fromId = labelOf(q.options, byId.get(q.question_id));
    if (fromId) return fromId;
    const line = questions.length > 1 ? typed[i] || "" : String(userText || "");
    const answer = questions.length > 1 && line.startsWith(`${q.text}${LINE_SEP}`) ? line.slice(q.text.length + LINE_SEP.length) : line;
    const norm = answer.trim().toLowerCase();
    return (q.options || []).find((o) => String(o?.label || "").trim().toLowerCase() === norm)?.label;
  });
}

/**
 * Replay (tse:join / reload / revive): the transcript row for an answer to a DA card, rebuilt from the recorded
 * `da_question` block (every question with its option ids + labels) and the answer turn's `da_answer` block (picked ids)
 * or, for records without one, its stored "Question — Answer" text. Same shapes the live path appends: an answered_batch
 * with every option per line, or an answered_question card. Null when the answer cannot be matched (plain bubble then).
 */
export function replayDaAnswer(cardBlock, answerBlock, userText) {
  const questions = (cardBlock?.questions || []).filter((q) => q && Array.isArray(q.options) && q.options.length);
  if (!questions.length) return null;
  const picked = pickedLabels(questions, answerBlock, userText);
  if (picked.some((p) => !p)) return null;
  if (questions.length > 1) {
    const lines = questions.map((q, i) => ({ question: q.text, answer: picked[i], options: q.options.map((o) => o.label) }));
    return { type: "answered_batch", sender: "user", text: String(userText || ""), lines };
  }
  const [q] = questions;
  const options = q.options.map((o) => o.label);
  return {
    type: "answered_question", sender: "agent", text: picked[0], selectedAnswer: picked[0],
    questionSnapshot: { question: q.text || cardBlock.text, options, pills: true },
  };
}
